import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    Pause,
    Play,
    Square,
    ArrowLeft,
    FileCheck2,
    ShieldCheck,
    Activity,
    Gauge,
    Clock,
    Database,
    AlertTriangle,
    Undo2,
    History,
    Repeat2,
    PlayCircle,
    Share2,
    Lock,
} from "lucide-react";
import { http, errMsg, formatBytes, formatDuration, splitBytes, splitSpeedMbps } from "@/lib/api";
import { toast } from "sonner";
import CountUp from "@/components/CountUp";
import { MetricInline } from "@/components/Metric";
import { StatusPill } from "@/pages/Dashboard";
import { useWS } from "@/context/WSContext";
import { useAuth } from "@/context/AuthContext";
import FlowVisualization from "@/components/FlowVisualization";
import ShareModal from "@/components/ShareModal";

const ROLE_RANK = { viewer: 1, auditor: 2, engineer: 3, admin: 4, superadmin: 5 };
const MAX_LOGS = 400;

export default function JobDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const { subscribe } = useWS();
    const [job, setJob] = useState(null);
    const [files, setFiles] = useState([]);
    const [logs, setLogs] = useState([]);
    const [versions, setVersions] = useState([]);
    const [errors, setErrors] = useState([]);
    const [err, setErr] = useState("");
    const [actionBusy, setActionBusy] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [showVersions, setShowVersions] = useState(false);
    const logEndRef = useRef(null);
    const navigate = useNavigate();

    const canAct = (ROLE_RANK[user?.role] || 0) >= ROLE_RANK.engineer;

    const load = async () => {
        try {
            const [j, f, l, v, e] = await Promise.all([
                http.get(`/jobs/${id}`),
                http.get(`/jobs/${id}/files?limit=200`),
                http.get(`/jobs/${id}/logs?limit=400`),
                http.get(`/jobs/${id}/versions`).catch(() => ({ data: [] })),
                http.get(`/jobs/${id}/errors?limit=100`).catch(() => ({ data: [] })),
            ]);
            setJob(j.data);
            setFiles(f.data);
            setLogs(l.data);
            setVersions(v.data);
            setErrors(e.data);
        } catch (e) {
            setErr(errMsg(e));
        }
    };
    const loadJob = () => load();

    useEffect(() => {
        load();
    }, [id]);

    useEffect(() => {
        const unsub = subscribe((m) => {
            if (!m?.data || m.data.job_id !== id) return;
            if (m.event === "job.progress" || m.event === "job.complete" || m.event === "job.update") {
                setJob((prev) => (prev ? { ...prev, ...m.data, status: m.data.status || prev.status } : prev));
                if (m.event === "job.complete") {
                    // reload files summary
                    http.get(`/jobs/${id}/files?limit=200`).then(({ data }) => setFiles(data));
                }
            } else if (m.event === "job.error") {
                setErrors((prev) => [m.data, ...prev].slice(0, 100));
                setJob((prev) => prev ? { ...prev, error_count: (prev.error_count || 0) + 1 } : prev);
            } else if (m.event === "job.log") {
                setLogs((prev) => {
                    const next = [...prev, m.data];
                    if (next.length > MAX_LOGS) next.splice(0, next.length - MAX_LOGS);
                    return next;
                });
            }
        });
        return unsub;
    }, [subscribe, id]);

    useEffect(() => {
        if (logEndRef.current) logEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [logs.length]);

    const doAction = async (action) => {
        setActionBusy(true);
        try {
            await http.post(`/jobs/${id}/${action}`);
            await load();
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setActionBusy(false);
        }
    };

    const doRollback = async () => {
        if (!window.confirm("Queue a rollback migration that will restore this job's source from destination?")) return;
        setActionBusy(true);
        try {
            const { data } = await http.post(`/jobs/${id}/rollback`, {});
            navigate(`/jobs/${data.id}`);
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setActionBusy(false);
        }
    };

    const doRetry = async () => {
        if (!window.confirm("Queue a fresh migration using the same source, destination, and options? The original job stays on record for audit.")) return;
        setActionBusy(true);
        try {
            const { data } = await http.post(`/jobs/${id}/retry`);
            navigate(`/jobs/${data.job_id}`);
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setActionBusy(false);
        }
    };

    const doResumeCheckpoint = async () => {
        if (!window.confirm("Resume this job from its last checkpoint? Only files that didn't verify on the previous run will be re-transferred.")) return;
        setActionBusy(true);
        try {
            const { data } = await http.post(`/jobs/${id}/resume-from-checkpoint`);
            toast.success(
                `Resumed — ${data.verified_files.toLocaleString()} files already verified, ${data.remaining_files.toLocaleString()} remaining.`,
            );
            await load();
        } catch (e) {
            setErr(errMsg(e));
            toast.error(errMsg(e));
        } finally {
            setActionBusy(false);
        }
    };

    if (err && !job) {
        return (
            <div className="p-6">
                <Link to="/jobs" className="text-xs text-muted-foreground hover:text-foreground">
                    ← Back to jobs
                </Link>
                <div className="mt-6 border border-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/5 p-4 text-sm text-[hsl(var(--destructive))]">
                    {err}
                </div>
            </div>
        );
    }

    if (!job) {
        return <div className="p-6 font-mono text-xs uppercase tracking-widest text-muted-foreground">Loading…</div>;
    }

    const progressPct = Math.min(100, (job.progress || 0) * 100);
    const speedCur = job.current_speed_mbps || 0;
    const speedAvg = job.avg_speed_mbps || 0;
    const isLive = ["running", "paused", "queued"].includes(job.status);
    const canRetry = ["failed", "canceled", "succeeded", "completed_with_errors"].includes(job.status);
    // Resume-from-checkpoint only makes sense when the job ended without
    // succeeding AND we actually have a manifest with un-verified files
    // (files_done < total_files). The endpoint validates server-side too.
    const canResumeCheckpoint =
        ["failed", "canceled", "completed_with_errors"].includes(job.status)
        && (job.total_files || 0) > 0
        && (job.files_done || 0) < (job.total_files || 0)
        && (job.files_done || 0) > 0;

    return (
        <div className="mx-auto max-w-[1600px] p-6">
            <Link
                to="/jobs"
                className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
                data-testid="job-back-link"
            >
                <ArrowLeft size={12} /> Jobs
            </Link>

            <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="mb-headline-lg">{job.name}</h1>
                        <StatusPill status={job.status} />
                    </div>
                    <div className="mt-1 font-mono text-[11px] text-muted-foreground">
                        {job.source_agent_name} <span className="text-[hsl(var(--accent))]">→</span> {job.dest_agent_name}
                        <span className="px-2">·</span>
                        {job.id}
                    </div>
                    {(job.source_storage_name || job.dest_storage_name) && (
                        <div className="mt-1 flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground" data-testid="job-storage-gateway-line">
                            <ShieldCheck size={11} className="text-emerald-400" />
                            {job.source_storage_name && (
                                <span>
                                    {job.source_agent_name} is the gateway for{" "}
                                    <span className="text-foreground/80">{job.source_storage_name}</span> ({job.source_storage_kind})
                                </span>
                            )}
                            {job.source_storage_name && job.dest_storage_name && <span className="px-1">·</span>}
                            {job.dest_storage_name && (
                                <span>
                                    {job.dest_agent_name} is the gateway for{" "}
                                    <span className="text-foreground/80">{job.dest_storage_name}</span> ({job.dest_storage_kind})
                                </span>
                            )}
                        </div>
                    )}
                </div>
                {canAct && (
                    <div className="flex items-center gap-2">
                        <ActionButton
                            label="Share"
                            icon={Share2}
                            onClick={() => setShareOpen(true)}
                            testId="job-share-btn"
                        />
                        <ActionButton
                            label="Pause"
                            icon={Pause}
                            disabled={!["running", "queued"].includes(job.status) || actionBusy}
                            onClick={() => doAction("pause")}
                            testId="job-pause-btn"
                        />
                        <ActionButton
                            label="Resume"
                            icon={Play}
                            disabled={job.status !== "paused" || actionBusy}
                            onClick={() => doAction("resume")}
                            testId="job-resume-btn"
                        />
                        <ActionButton
                            label="Cancel"
                            icon={Square}
                            danger
                            disabled={!isLive || actionBusy}
                            onClick={() => doAction("cancel")}
                            testId="job-cancel-btn"
                        />
                        {canResumeCheckpoint && (
                            <ActionButton
                                label="Resume checkpoint"
                                icon={PlayCircle}
                                disabled={actionBusy}
                                onClick={doResumeCheckpoint}
                                testId="job-resume-checkpoint-btn"
                            />
                        )}
                        {canRetry && (
                            <ActionButton
                                label="Retry"
                                icon={Repeat2}
                                disabled={actionBusy}
                                onClick={doRetry}
                                testId="job-retry-btn"
                            />
                        )}
                    </div>
                )}
            </div>

            {shareOpen && <ShareModal jobId={id} onClose={() => setShareOpen(false)} />}

            {/* Stuck-in-queue diagnostics — appears when the job has been queued
                for a while without a claim. The panel is lazy-loaded on demand. */}
            {job.status === "queued" && (
                <ClaimDiagnostics jobId={id} />
            )}

            {/* Error banner — shown whenever error_count > 0 */}
            {(job.error_count || 0) > 0 && (
                <div
                    className="mt-4 flex items-start justify-between gap-3 rounded-md border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/5 p-3"
                    data-testid="job-errors-banner"
                >
                    <div className="flex items-start gap-2">
                        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-[hsl(var(--destructive))]" />
                        <div>
                            <div className="text-sm font-semibold text-[hsl(var(--destructive))]">
                                {job.error_count} error{job.error_count > 1 ? "s" : ""} reported by the agent
                            </div>
                            <div className="text-[11px] text-muted-foreground">
                                Review affected files — the migration will continue retrying eligible ones.
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => setShowErrors(true)}
                        data-testid="view-errors-btn"
                        className="shrink-0 rounded-md border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 px-3 py-1.5 text-xs font-semibold text-[hsl(var(--destructive))] transition-colors hover:bg-[hsl(var(--destructive))]/20"
                    >
                        View errors
                    </button>
                </div>
            )}

            {showErrors && (
                <ErrorModal errors={errors} onClose={() => setShowErrors(false)} />
            )}

            {/* Source → Destination flow */}
            <div className="mt-5">
                <FlowVisualization
                    source={{ name: job.source_agent_name, hostname: job.source_path }}
                    destination={{ name: job.dest_agent_name, hostname: job.destination_path }}
                    status={job.status}
                    speedMbps={job.current_speed_mbps || job.avg_speed_mbps || 0}
                    progressPct={(job.progress || 0) * 100}
                    mode={job.is_rollback ? "rollback" : job.mode}
                />
            </div>

            {/* Live progress */}
            <div className="mt-5 border border-border bg-surface">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
                    <div className="flex items-center gap-2">
                        <Activity size={14} className="text-[hsl(var(--accent))]" />
                        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                            Live migration
                        </span>
                    </div>
                    <div className="flex items-center gap-5 font-mono text-[11px]">
                        <span>
                            <span className="text-muted-foreground">Speed: </span>
                            <span className="text-foreground" data-testid="job-speed-current">
                                <CountUp value={speedCur} decimals={1} /> MB/s
                            </span>{" "}
                            <span className="text-muted-foreground">avg <CountUp value={speedAvg} decimals={1} /></span>
                        </span>
                        <span>
                            <span className="text-muted-foreground">ETA: </span>
                            <span className="text-foreground" data-testid="job-eta">
                                {formatDuration(job.eta_seconds || 0)}
                            </span>
                        </span>
                        <span>
                            <span className="text-muted-foreground">Errors: </span>
                            <span className="text-foreground">{job.error_count || 0}</span>
                        </span>
                    </div>
                </div>
                <div className="p-5">
                    <div className="flex items-end justify-between">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                {job.files_done || 0} of {job.total_files} files ·{" "}
                                {formatBytes(job.bytes_done || 0)} / {formatBytes(job.total_bytes || 0)}
                            </div>
                            <div className="mt-1 flex items-baseline gap-2" data-testid="job-progress-pct">
                                <span className="mb-metric-lg" style={{ fontSize: "56px" }}>
                                    <CountUp value={progressPct} decimals={1} />
                                </span>
                                <span className="mb-metric-unit">%</span>
                            </div>
                        </div>
                        <div className="hidden sm:block text-right font-mono text-[11px] text-muted-foreground">
                            <div>{job.current_file ? `Processing ${job.current_file}` : "\u00A0"}</div>
                            <div>
                                Delta ratio: {job.delta_ratio != null ? `${(job.delta_ratio * 100).toFixed(0)}%` : "—"}
                            </div>
                        </div>
                    </div>
                    <div className="mt-4 h-2 overflow-hidden rounded-full bg-border">
                        <div
                            className="h-full bg-[hsl(var(--accent))] transition-all duration-500"
                            style={{ width: `${progressPct}%` }}
                            data-testid="job-progress-bar"
                        />
                    </div>
                </div>
            </div>

            {/* Stats grid */}
            <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-6">
                <StatCard icon={Database} label="Mode" value={job.is_rollback ? "rollback" : job.mode} />
                <StatCard icon={Gauge} label="Delta ratio" value={`${((job.delta_ratio || 1) * 100).toFixed(0)}%`} />
                <StatCard icon={Clock} label="Duration" value={formatDuration(job.duration_seconds || 0)} />
                <StatCard
                    icon={Repeat2}
                    label="Retries"
                    value={job.retry_count || 0}
                    accent={(job.retry_count || 0) > 0 ? "text-[hsl(var(--warning))]" : undefined}
                    testId="job-retry-count"
                />
                <StatCard
                    icon={ShieldCheck}
                    label="Integrity"
                    value={job.integrity_ok === false ? "Failed" : "OK (SHA-256)"}
                    accent={job.integrity_ok === false ? "text-[hsl(var(--destructive))]" : "text-[hsl(var(--success))]"}
                />
                <SecurityStatCard job={job} />
            </div>

            {(versions.length > 0 || job.is_rollback) && (
                <div className="mt-4 flex items-center gap-3 border border-border bg-surface px-4 py-3 text-sm">
                    <History size={14} className="text-[hsl(var(--accent))]" />
                    {job.is_rollback ? (
                        <span>
                            This job is a <span className="font-semibold">rollback</span> of{" "}
                            <Link
                                to={`/jobs/${job.rollback_of}`}
                                className="underline hover:text-brand"
                                data-testid="job-rollback-of-link"
                            >
                                {(job.rollback_of || "").slice(0, 8)}
                            </Link>
                            . Source/destination swapped.
                        </span>
                    ) : ["succeeded", "completed_with_errors"].includes(job.status) ? (
                        <span data-testid="job-versions-note">
                            <span className="font-semibold">{versions.length}</span> file versions snapshotted — rollback
                            available for this job.
                        </span>
                    ) : (
                        <span data-testid="job-versions-note">
                            <span className="font-semibold">{versions.length}</span> file versions recorded during this run.
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={() => setShowVersions(true)}
                        data-testid="btn-browse-versions"
                        className="mb-focus ml-auto rounded-md border border-border px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-foreground transition-colors hover:bg-foreground/5"
                    >
                        Browse & restore
                    </button>
                </div>
            )}

            {showVersions && (
                <VersionsModal
                    jobId={id}
                    versions={versions}
                    onClose={() => setShowVersions(false)}
                    onRestored={loadJob}
                />
            )}

            {/* Files + Logs */}
            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-5">
                <div className="border border-border bg-surface lg:col-span-2">
                    <div className="flex items-center justify-between border-b border-border px-4 py-3">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                Files
                            </div>
                            <div className="font-display text-lg font-bold">Manifest sample</div>
                        </div>
                        <FileCheck2 size={14} className="text-muted-foreground" />
                    </div>
                    <div className="scroll-thin max-h-[420px] overflow-y-auto">
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 border-b border-border bg-surface">
                                <tr className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                    <th className="px-3 py-2 text-left">Path</th>
                                    <th className="px-3 py-2 text-right">Size</th>
                                    <th className="px-3 py-2 text-right">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {files.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className="px-3 py-8 text-center text-muted-foreground">
                                            Manifest pending…
                                        </td>
                                    </tr>
                                )}
                                {files.map((f) => (
                                    <tr
                                        key={f.id}
                                        className="border-b border-border/60 last:border-0 hover:bg-secondary/30"
                                    >
                                        <td className="max-w-[280px] truncate px-3 py-1.5 font-mono">
                                            <span title={f.path}>{f.path}</span>
                                        </td>
                                        <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                                            {formatBytes(f.size)}
                                        </td>
                                        <td className="px-3 py-1.5 text-right">
                                            <span
                                                className={[
                                                    "mb-pill",
                                                    f.status === "verified"
                                                        ? "border-[hsl(var(--success))] text-[hsl(var(--success))]"
                                                        : f.status === "failed"
                                                          ? "border-[hsl(var(--destructive))] text-[hsl(var(--destructive))]"
                                                          : "border-border text-muted-foreground",
                                                ].join(" ")}
                                            >
                                                {f.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="border border-border bg-surface lg:col-span-3">
                    <div className="flex items-center justify-between border-b border-border px-4 py-3">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                Agent log stream
                            </div>
                            <div className="font-display text-lg font-bold">Terminal</div>
                        </div>
                        <span className="mb-pill border-[hsl(var(--accent))] text-[hsl(var(--accent))]">
                            <span className="mb-status-dot animate-pulse-dot bg-[hsl(var(--accent))]" />
                            live
                        </span>
                    </div>
                    <div className="mb-terminal scroll-thin h-[420px] overflow-y-auto p-3 text-xs leading-5" data-testid="job-logs-terminal">
                        {logs.length === 0 && (
                            <div className="text-muted-foreground">// waiting for agent…</div>
                        )}
                        {logs.map((l) => (
                            <div key={l.id} className="whitespace-pre-wrap">
                                <span className="log-ts">[{(l.timestamp || "").slice(11, 19)}]</span>{" "}
                                <span className={`log-${l.level}`}>{(l.level || "info").toUpperCase().padEnd(5)}</span>{" "}
                                <span>{l.message}</span>
                            </div>
                        ))}
                        <div ref={logEndRef} />
                    </div>
                </div>
            </div>

            {err && (
                <div className="mt-4 flex items-center gap-2 rounded-sm border border-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/5 p-3 text-xs text-[hsl(var(--destructive))]">
                    <AlertTriangle size={13} /> {err}
                </div>
            )}
        </div>
    );
}

function ActionButton({ label, icon: Icon, onClick, disabled, danger, testId }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            data-testid={testId}
            className={[
                "inline-flex items-center gap-1.5 rounded-sm border px-3 py-2 text-xs font-semibold uppercase tracking-widest transition-colors disabled:opacity-40",
                danger
                    ? "border-border hover:border-[hsl(var(--destructive))] hover:text-[hsl(var(--destructive))]"
                    : "border-border hover:border-brand hover:text-brand",
            ].join(" ")}
        >
            <Icon size={13} />
            {label}
        </button>
    );
}

function StatCard({ icon: Icon, label, value, accent, testId, title }) {
    return (
        <div className="border border-border bg-surface p-4" data-testid={testId} title={title}>
            <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
                <Icon size={14} className={accent || "text-muted-foreground"} />
            </div>
            <div className={`mt-2 font-display text-xl font-bold capitalize ${accent || ""}`}>{value || "—"}</div>
        </div>
    );
}

// Quantum-Safe Transfer: renders the negotiated profile the agent reported
// via POST /agents/progress (job.security_profile / _key_establishment /
// _post_quantum / _downgrade — see backend routers/agents.py::AgentProgressIn
// and agent-csharp FileTransferService.ReportProgress). Absent on jobs run by
// pre-PQC agent builds or before negotiation completes — renders "—", not a
// false "Standard".
function SecurityStatCard({ job }) {
    const profile = job.security_profile;
    const value = profile === "QuantumSafeHybrid" ? "Quantum-Safe" : profile === "Standard" ? "Standard" : null;
    const accent = profile === "QuantumSafeHybrid"
        ? "text-[hsl(var(--success))]"
        : job.security_downgrade
          ? "text-[hsl(var(--warning))]"
          : undefined;
    const title = profile
        ? [
            `Key establishment: ${job.security_key_establishment || "—"}`,
            job.security_downgrade ? "Fell back from the requested profile" : null,
          ].filter(Boolean).join(" · ")
        : "Not yet reported by the agent";
    return (
        <StatCard
            icon={Lock}
            label="Security"
            value={value}
            accent={accent}
            title={title}
            testId="job-security-profile"
        />
    );
}


function ErrorModal({ errors, onClose }) {
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={onClose}
            data-testid="error-modal-overlay"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="relative mx-4 flex max-h-[80vh] w-full max-w-2xl flex-col rounded-xl border border-border bg-surface"
                data-testid="error-modal"
            >
                <div className="flex items-center justify-between border-b border-border p-4">
                    <div className="flex items-center gap-2">
                        <AlertTriangle size={16} className="text-[hsl(var(--destructive))]" />
                        <h3 className="font-display text-lg font-bold">Errors ({errors.length})</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                        ✕
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                    {errors.length === 0 && (
                        <div className="py-8 text-center text-sm text-muted-foreground">
                            No error details reported by the agent yet.
                        </div>
                    )}
                    <ul className="space-y-2" data-testid="error-list">
                        {errors.map((e, i) => (
                            <li
                                key={e.id || i}
                                className="rounded-md border border-border bg-background p-3"
                                data-testid={`error-row-${i}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            {e.error_code && (
                                                <span className="rounded bg-[hsl(var(--destructive))]/10 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase text-[hsl(var(--destructive))]">
                                                    {e.error_code}
                                                </span>
                                            )}
                                            {e.file_path && (
                                                <code className="truncate font-mono text-[11px] text-muted-foreground">
                                                    {e.file_path}
                                                </code>
                                            )}
                                        </div>
                                        <div className="mt-1 text-sm">{e.message}</div>
                                    </div>
                                    <div className="shrink-0 font-mono text-[10px] text-muted-foreground">
                                        {(e.timestamp || "").slice(11, 19) || "—"}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

// ==================== VersionsModal ====================
function VersionsModal({ jobId, versions, onClose, onRestored }) {
    const [busy, setBusy] = useState(null);   // version_id being restored
    const [err, setErr] = useState("");
    const [toast, setToast] = useState("");

    // Group by rel_path for clearer display
    const grouped = React.useMemo(() => {
        const m = new Map();
        (versions || []).forEach((v) => {
            if (!v) return;
            const key = v.dest_path || v.rel_path || v.file_path || "(unknown)";
            if (!m.has(key)) m.set(key, []);
            m.get(key).push(v);
        });
        // newest-first inside each group
        for (const arr of m.values()) {
            arr.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
        }
        return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
    }, [versions]);

    const restore = async (v) => {
        if (!window.confirm(`Restore "${v.dest_path || v.rel_path}" to this version?\n\nThe current file on the destination will be kept as a new version before overwriting.`)) return;
        setErr("");
        setBusy(v.id);
        try {
            await http.post(`/jobs/${jobId}/versions/${v.id}/restore`);
            setToast(`Restore queued for ${v.dest_path || v.rel_path}`);
            onRestored && onRestored();
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setBusy(null);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            onClick={onClose}
            data-testid="versions-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="max-h-[85vh] w-full max-w-3xl overflow-hidden rounded-lg border border-border bg-surface shadow-2xl"
            >
                <div className="flex items-center justify-between border-b border-border px-5 py-3">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Versions & rollback</div>
                        <div className="mb-headline">File versions ({versions?.length || 0})</div>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="versions-modal-close"
                        className="rounded-md p-2 text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
                    >
                        ✕
                    </button>
                </div>
                {err && (
                    <div className="border-b border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/10 px-5 py-2 text-xs text-[hsl(var(--destructive))]">
                        {err}
                    </div>
                )}
                {toast && (
                    <div className="border-b border-emerald-400/30 bg-emerald-400/10 px-5 py-2 text-xs text-emerald-300">
                        {toast}
                    </div>
                )}
                <div className="max-h-[65vh] overflow-y-auto">
                    {grouped.length === 0 ? (
                        <div className="p-10 text-center text-sm text-muted-foreground">
                            No versions recorded for this job yet.
                        </div>
                    ) : grouped.map(([path, rows]) => (
                        <div key={path} className="border-b border-border last:border-b-0">
                            <div className="flex items-center justify-between bg-foreground/[0.02] px-5 py-2">
                                <div className="truncate font-mono text-xs text-foreground" title={path}>{path}</div>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                    {rows.length} version{rows.length !== 1 ? "s" : ""}
                                </div>
                            </div>
                            <ul>
                                {rows.map((v) => (
                                    <li
                                        key={v.id}
                                        data-testid={`version-row-${v.id}`}
                                        className="flex items-center gap-3 px-5 py-2 text-xs hover:bg-foreground/[0.02]"
                                    >
                                        <div className="w-14 shrink-0 font-mono text-[11px] text-muted-foreground">
                                            v{v.version ?? "—"}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="font-mono text-[11px] text-foreground">
                                                {(v.created_at || "").replace("T", " ").slice(0, 19)}
                                            </div>
                                            <div className="font-mono text-[10px] text-muted-foreground">
                                                {v.size != null ? `${(v.size / 1024).toFixed(1)} KB` : ""}
                                                {v.hash ? ` · sha256:${String(v.hash).slice(0, 12)}…` : ""}
                                                {v.restored ? " · restored" : ""}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            disabled={busy === v.id}
                                            onClick={() => restore(v)}
                                            data-testid={`version-restore-${v.id}`}
                                            className="mb-focus rounded-md bg-gradient-to-r from-[#004AAD] to-[#00C2FF] px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white shadow transition-all hover:brightness-110 disabled:opacity-50"
                                        >
                                            {busy === v.id ? "Queuing…" : "Restore"}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ClaimDiagnostics({ jobId }) {
    // Why isn't my queued job being claimed? This panel calls the
    // /jobs/{id}/diagnostics endpoint every 8 s and renders a plain
    // explanation of both agents' claim-gate state. First line of
    // support when a real PC→PC migration stalls.
    const [data, setData] = React.useState(null);
    const [err, setErr] = React.useState("");

    React.useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const res = await http.get(`/jobs/${jobId}/diagnostics`);
                if (alive) { setData(res.data); setErr(""); }
            } catch (e) {
                if (alive) setErr(errMsg(e));
            }
        };
        load();
        const iv = setInterval(load, 8000);
        return () => { alive = false; clearInterval(iv); };
    }, [jobId]);

    if (err) return null;
    if (!data) return null;

    const tone = data.would_claim
        ? "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/5"
        : "border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/5";

    return (
        <div
            className={`mt-4 rounded-md border ${tone} p-4`}
            data-testid="job-claim-diagnostics"
        >
            <div className="flex items-center gap-2">
                <AlertTriangle size={14} className="shrink-0 text-[hsl(var(--warning))]" />
                <div className="text-sm font-semibold">
                    Waiting for an agent to claim this migration
                </div>
            </div>
            <ul className="mt-2 space-y-1 pl-6 text-[12px] text-muted-foreground">
                {(data.summary || []).map((s, i) => (
                    <li key={`s-${i}-${(s || "").slice(0, 16)}`} className="list-disc">{s}</li>
                ))}
            </ul>

            <div className="mt-3 grid gap-3 md:grid-cols-2">
                <AgentDiagCard info={data.source} />
                <AgentDiagCard info={data.destination} />
            </div>
        </div>
    );
}

function AgentDiagCard({ info }) {
    if (!info) return null;
    const age = info.last_heartbeat_age_sec;
    const ageLabel =
        age == null ? "never" :
        age < 60 ? `${age}s ago` :
        age < 3600 ? `${Math.round(age / 60)}m ago` :
        `${Math.round(age / 3600)}h ago`;
    return (
        <div className="rounded-md border border-border bg-background/40 p-3">
            <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {info.label}
                </div>
                <span
                    className={[
                        "rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest",
                        info.ok
                            ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]"
                            : "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))]",
                    ].join(" ")}
                >
                    {info.ok ? "ready" : "blocked"}
                </span>
            </div>
            <div className="mt-1 text-sm font-semibold">{info.name || "—"}</div>
            <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                <div>status: <span className="text-foreground">{info.status || "—"}</span></div>
                <div>role: <span className="text-foreground">{info.role || "—"}</span></div>
                <div>kind: <span className="text-foreground">{info.kind || "—"}</span></div>
                <div>last seen: <span className="text-foreground">{ageLabel}</span></div>
                {info.last_claim_poll_age_sec != null && (
                    <div className="col-span-2">
                        last <code>/claim</code> poll:{" "}
                        <span className="text-foreground">
                            {info.last_claim_poll_age_sec < 60
                                ? `${info.last_claim_poll_age_sec}s ago`
                                : `${Math.round(info.last_claim_poll_age_sec / 60)}m ago`}
                            {info.claim_poll_count ? ` · ${info.claim_poll_count} total` : ""}
                        </span>
                    </div>
                )}
                {info.last_claim_poll_age_sec == null && info.label === "source" && (
                    <div className="col-span-2 text-[hsl(var(--warning))]">
                        last <code>/claim</code> poll: <b>never seen</b>
                    </div>
                )}
            </div>
            {(info.blockers?.length > 0 || info.reason) && (
                <ul className="mt-2 space-y-0.5 pl-4 text-[11.5px] text-[hsl(var(--destructive))]">
                    {info.reason && <li className="list-disc">{info.reason}</li>}
                    {(info.blockers || []).map((iss, i) => (
                        <li key={`b-${i}-${(iss || "").slice(0, 16)}`} className="list-disc">{iss}</li>
                    ))}
                </ul>
            )}
            {(info.notes?.length > 0) && (
                <ul className="mt-2 space-y-0.5 pl-4 text-[11.5px] text-muted-foreground">
                    {info.notes.map((n, i) => (
                        <li key={`n-${i}-${(n || "").slice(0, 16)}`} className="list-disc">{n}</li>
                    ))}
                </ul>
            )}
        </div>
    );
}

